import { useWatch } from 'react-hook-form'
import { useGqlExamCompanyQuery } from 'generated/graphql'
import { TOptionsValue } from '@app.feature/careerManage/desktop/careerContent/components/inputFields/select/components/SelectOption'
import { TPreReservationFormValues } from '../constants/form'

type TFormatSelectOptionsParams = {
  id: string
  name: string
  [key: string]: any
}

type TUsePredictionSelectOptionsReturns = {
  subsidiaries: Array<TOptionsValue>
  departments: Array<TOptionsValue>
  jobs: Array<TOptionsValue>
}

type TUseGetPredictionSelectOptionsFnParams = {
  companyId: string
  termId: string
}

type TUseGetPredictionSelectOptionsFn = (
  params: TUseGetPredictionSelectOptionsFnParams,
) => TUsePredictionSelectOptionsReturns

const useGetPredictionSelectOptions: TUseGetPredictionSelectOptionsFn = ({
  companyId,
  termId,
}) => {
  const { data, loading, error } = useGqlExamCompanyQuery({
    variables: {
      examCompanyId: Number(companyId),
      termId,
    },
  })

  const [subsidiaryId, departmentId] = useWatch<
    TPreReservationFormValues,
    ['subsidiaryId', 'departmentId']
  >({
    name: ['subsidiaryId', 'departmentId'],
  })

  const getSubsidiaries = (
    subsidiaries?: Array<TFormatSelectOptionsParams>,
  ) => {
    if (!subsidiaries) return []
    return subsidiaries?.map((subsidiary) => ({
      label: subsidiary.name,
      value: subsidiary.id,
    }))
  }

  const getDepartments = (subsidiaries?: Array<TFormatSelectOptionsParams>) => {
    if (!subsidiaryId || !subsidiaries) return []
    const subsidiary = subsidiaries?.find((sub) => sub.id === subsidiaryId)
    return subsidiary?.departments?.map((department) => ({
      label: department.name,
      value: department.id,
    }))
  }

  const getJobs = (subsidiaries?: Array<TFormatSelectOptionsParams>) => {
    if (!subsidiaryId || !subsidiaries || !departmentId) return []
    const subsidiary = subsidiaries?.find((sub) => sub.id === subsidiaryId)

    return subsidiary?.departments
      ?.find((department) => department.id === departmentId)
      ?.jobs.map((job) => ({
        label: job.name,
        value: job.id,
      }))
  }

  if (!loading || !error || data?.examCompany) {
    return {
      subsidiaries: getSubsidiaries(data?.examCompany?.subsidiaries),
      departments: getDepartments(data?.examCompany?.subsidiaries),
      jobs: getJobs(data?.examCompany?.subsidiaries),
    }
  } else {
    return {
      subsidiaries: [],
      departments: [],
      jobs: [],
    }
  }
}

export default useGetPredictionSelectOptions
